import { z } from "zod/v4"
import { CreatedAt, UpdatedAt } from "./system"

//
// ===== Requirement =====
//

export const AppConfigRequirementDocumentValue = z.object({
  min_version: z.string().describe("利用できる最低のバージョン。"),
})

export const AppConfigRequirementDocumentMeta = z.object({
  ios: AppConfigRequirementDocumentValue,
  android: AppConfigRequirementDocumentValue,
})

export type AppConfigRequirementDocumentMeta = z.infer<typeof AppConfigRequirementDocumentMeta>

export const SetAppConfigRequirementSingleRequest = z.object({
  minVersion: z.string(),
})

export const SetAppConfigRequirementRequest = z.object({
  ios: SetAppConfigRequirementSingleRequest,
  android: SetAppConfigRequirementSingleRequest,
})
export type SetAppConfigRequirementRequest = z.infer<typeof SetAppConfigRequirementRequest>

export const AppConfigRequirementDocumentBody = z.intersection(
  AppConfigRequirementDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)
export type AppConfigRequirementDocumentBody = z.infer<typeof AppConfigRequirementDocumentBody>

//
// ===== GlobalNavigation =====
//

export const SetAppConfigGlobalNavigationShopRequest = z.object({
  unreadAt: z.number().nullable().describe("ショップの未読フラグの更新日時（Unix timestamp ms）。nullの場合はバッジ無効。"),
})
export type SetAppConfigGlobalNavigationShopRequest = z.infer<typeof SetAppConfigGlobalNavigationShopRequest>

export const SetAppConfigGlobalNavigationRequest = z.object({
  shop: SetAppConfigGlobalNavigationShopRequest,
})
export type SetAppConfigGlobalNavigationRequest = z.infer<typeof SetAppConfigGlobalNavigationRequest>

export const AppConfigGlobalNavigationShop = z.object({
  unread_at: z.number().describe("ショップの未読フラグの更新日時（Unix timestamp ms）。"),
  expires_at: z.number().describe("ショップの赤バッジの有効期限（Unix timestamp ms）。unread_atから24時間後。"),
}).describe("ショップのグローバルナビゲーション設定。バッジ無効時はshopフィールド自体がundefined。")
export type AppConfigGlobalNavigationShop = z.infer<typeof AppConfigGlobalNavigationShop>

export const AppConfigGlobalNavigationDocumentMeta = z.object({
  shop: AppConfigGlobalNavigationShop.optional().describe("ショップのグローバルナビゲーション設定"),
}).describe("グローバルナビゲーションの情報を示すオブジェクトです。")

export const AppConfigGlobalNavigationDocumentBody = z.intersection(
  AppConfigGlobalNavigationDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type AppConfigGlobalNavigationDocumentBody = z.infer<typeof AppConfigGlobalNavigationDocumentBody>

//
// ===== Terms (規約同意管理) =====
//

/**
 * 規約の種類
 * - terms-of-service: 利用規約
 * - privacy: プライバシーポリシー
 */
export const TermsAgreementKind = z.enum(["terms-of-service", "privacy"])
export type TermsAgreementKind = z.infer<typeof TermsAgreementKind>

/**
 * セマンティックバージョン形式のバリデーション（例: 1.0.0, 0.1.10, 2.0.0）
 */
const SemverVersion = z.string().regex(/^\d+\.\d+\.\d+$/, "バージョンは X.Y.Z 形式で入力してください（例: 1.0.0）")

/**
 * 規約バージョン設定リクエスト（管理API用）
 */
export const SetAppConfigTermsAgreementRequest = z.object({
  kind: TermsAgreementKind,
  version: SemverVersion.describe("規約のバージョン（セマンティックバージョニング形式）"),
  effectiveAt: z.number().describe("規約の有効開始日時（Unix timestamp ms）"),
})
export type SetAppConfigTermsAgreementRequest = z.infer<typeof SetAppConfigTermsAgreementRequest>

/**
 * 規約同意リクエスト（クライアントAPI用）
 */
export const AgreeToTermsRequest = z.object({
  kind: TermsAgreementKind,
})
export type AgreeToTermsRequest = z.infer<typeof AgreeToTermsRequest>

/**
 * 各規約タイプのバージョン情報（単一エントリ）
 */
export const AppConfigTermsAgreementValue = z.object({
  version: SemverVersion.describe("規約のバージョン（セマンティックバージョニング形式）"),
  effective_at: z.number().describe("規約の有効開始日時（Unix timestamp ms）。この日時以降、クライアントは同意を求めます。"),
})
export type AppConfigTermsAgreementValue = z.infer<typeof AppConfigTermsAgreementValue>

/**
 * /app-configs/terms ドキュメントのメタデータ
 * 各規約タイプをキーとし、バージョン履歴の配列を値として持つマップ構造。
 * 配列は effective_at の昇順でソートされている。
 * すべてのキーが存在するとは限らない。
 */
export const AppConfigTermsDocumentMeta = z.object({
  "terms-of-service": z.array(AppConfigTermsAgreementValue).optional(),
  "privacy": z.array(AppConfigTermsAgreementValue).optional(),
})
export type AppConfigTermsDocumentMeta = z.infer<typeof AppConfigTermsDocumentMeta>

export const AppConfigTermsDocumentBody = z.intersection(
  AppConfigTermsDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)
export type AppConfigTermsDocumentBody = z.infer<typeof AppConfigTermsDocumentBody>

export class AppConfigDocuments {

  static readonly requirementId = "requirement"
  static readonly globalNavigationId = "global-navigation"
  static readonly termsId = "terms"
  static readonly maintenanceId = "maintenance"
  static readonly geoIpCidrId = "geo-ip-cidr"
  static readonly conversionRateId = "conversion-rate"

}
